import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { RequestFailed } from "../../components/RequestFailed";
import { Global } from "@emotion/react";
import {
	Avatar as CRAvatar,
	Button,
	Divider,
	Heading,
	HStack,
	Link,
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Progress,
	Stack,
	VStack,
	Alert,
	// AlertIcon,
	useBreakpointValue,
	Icon,
	Tabs,
	TabList,
	TabPanels,
	TabPanel,
	Tab,
} from "@chakra-ui/react";
import {
	ArrowUpRight,
	Check,
	EditIcon,
	Plus,
	Share,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";
import clsx from "clsx";
import { WorkHistory } from "../../features/pro-portfolio/work-history";
import { EmploymentHistory } from "../../features/pro-portfolio/employment-history";
import { WorkHistoryEditor } from "../../features/pro-portfolio/work-history-editor";
import { EmploymentHistoryEditor } from "../../features/pro-portfolio/employment-history/employment-history-editor";
import { Rating } from "../../features/pro-portfolio/rating";
import { Helmet } from "react-helmet";
import { FaFacebookF, FaTwitter } from "react-icons/fa";
import { logEvent } from "src/utlis/analytics";
import { useAuth0 } from "@auth0/auth0-react";
import { PortfolioBadgeSection } from "../../features/pro-portfolio/badge-section";
import { PortfolioSectionHeading } from "../../features/pro-portfolio/portfolio-section-heading";
import { CertificationCreator } from "../../features/pro-portfolio/certification-creator";
import { Certifications } from "../../features/pro-portfolio/certifications";
import HelmetIcon from "../../assets/helmet-icon.png";
import TransactionsIcon from "../../assets/transactions-icon.png";
import Slider from "react-slick";
import { formatAsNaira, getCappedAmount } from "src/utlis/helpers";
import { ProNotFound } from "src/features/pro-portfolio/pro-not-found";
import { NothingToSeeYet } from "src/features/pro-portfolio/nothing-to-see-yet";
import { IoLogOutOutline } from "react-icons/io5";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useProfile } from "../../hooks/useProfile";
import { PortfolioProfile } from "../../features/pro-portfolio/profile";
import { EducationEntries } from "src/features/pro-portfolio/education-entries";
import { GET_MY_PORTFOLIO_WITH_MEDIA } from "../../common/graphql/typed-queries";
import { PortfolioWalkthrough } from "../../features/pro-portfolio/walkthrough";
import JoinWhatsAppCommunityModal from "@/components/JoinWhatsAppCommunityModal";
import PortfolioImageContainer, {
	PortfolioImage,
} from "src/features/pro-portfolio/portfolio-image";
import { cloneDeep } from "@apollo/client/utilities";
import { useToggles } from "../../hooks/useToggles";

export default function ProProfilePage() {
	const { isLoading: isLoadingToggles, toggles } = useToggles();

	const hasAcknowledgedPortfolioWalkthrough = localStorage.getItem(
		"hasAcknowledgedPortfolioWalkthrough"
	);

	const hasClickedCTAToJoinWhatsAppCommunity = localStorage.getItem(
		"hasClickedCTAToJoinWhatsAppCommunity"
	);

	const [showWalkthrough, setShowWalkthrough] = React.useState(
		!hasAcknowledgedPortfolioWalkthrough
	);

	const [showJoinWhatsAppCommunityModal, setShowJoinWhatsAppCommunityModal] =
		React.useState(false);

	const getProfileHandle = useProfile();

	const navigate = useNavigate();

	const { isAuthenticated, user } = useAuth0();

	const [copied, setCopied] = React.useState(false);

	const [showShareModal, setShowShareModal] = React.useState(false);

	const [inEditView, setInEditView] = React.useState<boolean>(false);

	const [showCertificationCreateModal, setShowCertificationCreateModal] =
		React.useState(false);

	const [currentSlide, setCurrentSlide] = React.useState(0);

	const [editingWorkItem, setEditingWorkItem] = React.useState<{
		id: string;
		type: "project-history-item" | "employment-history-item";
	} | null>(null);

	const handleEditFromGallery = (
		/**
		 * The id of the project or employment history item
		 */
		itemId: string,
		itemType: "project-history-item" | "employment-history-item"
	) => {
		setEditingWorkItem({ id: itemId, type: itemType });
	};

	const handleCloseEditModal = () => {
		setEditingWorkItem(null);
	};

	// Custom arrow components
	const CustomPrevArrow = (props: any) => {
		const { onClick } = props;
		return (
			<div
				className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow"
				onClick={onClick}
			>
				<ChevronLeft className="w-6 h-6 text-gray-700" />
			</div>
		);
	};

	const CustomNextArrow = (props: any) => {
		const { onClick } = props;
		return (
			<div
				className="absolute right-1 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow"
				onClick={onClick}
			>
				<ChevronRight className="w-6 h-6 text-gray-700" />
			</div>
		);
	};

	const carouselSettings = useBreakpointValue({
		base: {
			slidesToShow: 1,
			prevArrow: <CustomPrevArrow />,
			nextArrow: <CustomNextArrow />,
		},
		lg: {
			prevArrow: <CustomPrevArrow />,
			nextArrow: <CustomNextArrow />,
		},
	});

	React.useEffect(() => {
		if (copied) {
			setTimeout(() => {
				setCopied(false);
			}, 2000);
		}
	}, [copied]);

	const {
		data: portfolioDataWithoutMedia,
		loading: loadingPortfolioWithoutMedia,
		error: portfolioWithoutMediaError,
	} = useQuery(GET_MY_PORTFOLIO_WITH_MEDIA, { fetchPolicy: "cache-and-network" });

	const isPro =
		isAuthenticated &&
		user?.email === portfolioDataWithoutMedia?.myPortfolio?.email;

	React.useEffect(() => {
		if (isPro) {
			setInEditView(true);
		}
	}, [isPro]);

	const projectHistoryMedia = useMemo(() => {
		const portfolio = portfolioDataWithoutMedia?.myPortfolio;

		if (!portfolio) return [];

		const cloned = cloneDeep(portfolio.workHistory);

		const items: PortfolioImage[] = [];

		cloned.forEach((item) => {
			item.images.forEach((image) => {
				if (!image.originalUrl || !image.thumbnailUrl) return;

				items.push({
					name: item.name,
					type: image.mimeType,
					urls: {
						originalUrl: image.originalUrl,
						thumbnailUrl: image.thumbnailUrl,
					},
					itemId: item.id || undefined,
					itemType: "project-history-item",
				});
			});
		});

		return items;
	}, [portfolioDataWithoutMedia?.myPortfolio]);

	const employmentHistoryMedia = useMemo(() => {
		const portfolio = portfolioDataWithoutMedia?.myPortfolio;

		if (!portfolio) return [];

		const cloned = cloneDeep(portfolio.employmentHistory);

		const items: PortfolioImage[] = [];

		cloned.forEach((item) => {
			item.images.forEach((image) => {
				if (!image.originalUrl || !image.thumbnailUrl) return;

				items.push({
					name: item.jobTitle,
					type: image.mimeType,
					urls: {
						originalUrl: image.originalUrl,
						thumbnailUrl: image.thumbnailUrl,
					},
					itemId: item.id,
					itemType: "employment-history-item",
				});
			});
		});

		return items;
	}, [portfolioDataWithoutMedia?.myPortfolio]);

	if (
		loadingPortfolioWithoutMedia ||
		getProfileHandle.loading ||
		isLoadingToggles
	)
		return <Loading />;

	if (portfolioWithoutMediaError || getProfileHandle.error)
		return <RequestFailed />;

	if (!portfolioDataWithoutMedia || !getProfileHandle.profile)
		return <RequestFailed />;

	if (!portfolioDataWithoutMedia.myPortfolio) return <ProNotFound />;

	const canEdit = inEditView;

	const noOfJobsCompleted =
		portfolioDataWithoutMedia.myPortfolio.workHistory.length;

	const noOfEmploymentHistoryItems =
		portfolioDataWithoutMedia.myPortfolio.employmentHistory.length;

	const noOfJobsWithBudget =
		portfolioDataWithoutMedia.myPortfolio.workHistory.filter(
			(item) => !!item.budget
		).length;

	const pro = getProfileHandle.profile;

	const totalBudget = portfolioDataWithoutMedia.myPortfolio.totalBudget;

	const averageBudget = totalBudget > 0 ? totalBudget / noOfJobsWithBudget : 0;

	const portfolio = portfolioDataWithoutMedia.myPortfolio;

	const portfolioMedia = [...projectHistoryMedia, ...employmentHistoryMedia];

	const shortenedName = `${portfolio.firstName} ${portfolio.lastName[0]}.`;

	const linkToPublicView = `${process.env.REACT_APP_HOME_APP_URL}/pros/@${portfolio.handle}`;

	const isModifyingToApplyForAJob = !!localStorage.getItem(
		"redirectAferPortfolioEditForJobApplication"
	);

	const redirectToJobApplicationAfterModification = () => {
		const redirect = localStorage.getItem(
			"redirectAferPortfolioEditForJobApplication"
		);
		if (!!redirect) {
			setTimeout(() => {
				localStorage.removeItem("redirectAferPortfolioEditForJobApplication");

				navigate(redirect);
			}, 1000);
		}
	};

	const handleAcknowledgeWalkthrough = () => {
		logEvent("pro_portfolio/portfolio_walkthrough_acknowledged");

		localStorage.setItem("hasAcknowledgedPortfolioWalkthrough", "yes");

		setShowWalkthrough(false);
	};

	const handleShare = () => {
		logEvent("pro_portfolio/share_profile_clicked");

		if (!portfolio) return;

		/**
		 * Open the Web Share API
		 */
		if (navigator.share) {
			navigator
				.share({
					title: `${shortenedName} | LaborHack Pro`,
					text: `Check out ${shortenedName}'s profile on LaborHack`,
					url: linkToPublicView,
				})
				.then(() => {
					logEvent("pro_portfolio/profile_shared");
				})
				.catch((error) => {
					if (error.name === "AbortError") {
						return;
					}

					logEvent("pro_portfolio/profile_share_failed", {
						error: error.message,
					});

					setShowShareModal(true);
				});
		} else {
			setShowShareModal(true);
		}
	};

	const steps = [
		{
			name: "Personal Information",
			isComplete: true,
			isRequired: true,
		},
		{
			name: "Add Work History",
			isComplete: noOfJobsCompleted > 0 || noOfEmploymentHistoryItems > 0,
			isRequired: true,
			cta: {
				text: "Add",
				url: "/my-laborpass/add-job",
			},
		},
		{
			name: "Generate Profile",
			isComplete: !!pro.overview,
			isRequired: true,
		},
		{
			name: "Add Certifications",
			isComplete: portfolio.certifications.length > 0,
			cta: {
				text: "Add",
				url: "/my-laborpass/add-certification",
			},
		},
		{
			name: "Add Education",
			isComplete: portfolio.educationEntries.length > 0,
			cta: {
				text: "Add",
				url: "/my-laborpass/add-education",
			},
		},
	];

	const allRequiredStepsComplete = steps
		.filter((step) => step.isRequired)
		.every((step) => step.isComplete);

	const progress =
		(steps.filter((step) => step.isComplete).length / steps.length) * 100;

	let experience: string;

	if (portfolio.experience && portfolio.experience > 0) {
		experience =
			portfolio.experience > 1
				? `${portfolio.experience} years of experience`
				: `${portfolio.experience} year of experience`;
	} else if (portfolio.experience === 0) {
		experience = "< 1 year of experience";
	} else {
		experience = "No experience set";
	}

	const exitLaborPass = () => {
		if (
			toggles.show_join_outreach_whatsapp_gc_dialog &&
			!hasClickedCTAToJoinWhatsAppCommunity
		) {
			setShowJoinWhatsAppCommunityModal(true);
		} else {
			navigate("/");
		}
	};

	return (
		<>
			<Helmet>
				<title>
					{portfolio.firstName} {portfolio.lastName?.[0]}. | LaborHack
				</title>
			</Helmet>
			<Global
				styles={`
       body {
          background-color: white !important;
        }
      `}
			/>
			{showWalkthrough && (
				<PortfolioWalkthrough onClose={handleAcknowledgeWalkthrough} />
			)}

			<JoinWhatsAppCommunityModal
				isOpen={
					showJoinWhatsAppCommunityModal &&
					toggles.show_join_outreach_whatsapp_gc_dialog
				}
				onClose={() => {
					navigate("/");
				}}
			/>

			<Modal
				isOpen={showCertificationCreateModal}
				onClose={() => setShowCertificationCreateModal(false)}
				isCentered
				closeOnOverlayClick
				size="xl"
			>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader className="font-[AeonikPro] !text-2xl">
						Add Certification
					</ModalHeader>
					<Divider />
					<VStack py={4} px={6} alignItems="start">
						<CertificationCreator
							onClose={() => setShowCertificationCreateModal(false)}
						/>
					</VStack>
				</ModalContent>
			</Modal>

			<Modal
				isOpen={showShareModal}
				onClose={() => setShowShareModal(false)}
				isCentered
				closeOnOverlayClick
			>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Share Profile</ModalHeader>
					<VStack pb={8} px={6} alignItems="start">
						<HStack>
							<Link
								href={`https://twitter.com/intent/tweet?text=Check out ${portfolio.firstName} ${portfolio.lastName[0]} on LaborHack&url=${linkToPublicView}`}
								isExternal
							>
								<Button
									rightIcon={<FaTwitter />}
									colorScheme="twitter"
									variant="solid"
									onClick={() =>
										logEvent("pro_portfolio/twitter_share_clicked")
									}
								>
									Tweet
								</Button>
							</Link>
							<Link
								href={
									"https://www.facebook.com/sharer/sharer.php?u=" +
									encodeURIComponent(linkToPublicView)
								}
								isExternal
							>
								<Button
									rightIcon={<FaFacebookF />}
									colorScheme="facebook"
									variant="solid"
									onClick={() =>
										logEvent("pro_portfolio/facebook_share_clicked")
									}
								>
									Post to Facebook
								</Button>
							</Link>
						</HStack>
					</VStack>
				</ModalContent>
			</Modal>

			<HStack w="full" bgColor="white" justifyContent="center">
				<Stack
					px={{
						base: 0,
						lg: 5,
					}}
					py={{
						base: 8,
						lg: 18,
					}}
					spacing={{
						base: 8,
						lg: 8,
					}}
					direction="column"
					w="full"
					maxW="container.xl"
					alignItems={{
						base: "start",
						lg: "center",
					}}
				>
					<div className="px-4 lg:px-0 w-full">
						<HStack
							borderBottom={"1px"}
							borderColor={"#E7E7E7"}
							py={3}
							w={"full"}
							textColor={"#8F8F8F"}
						>
							<Button
								variant="ghost"
								colorScheme="earthRed"
								display={"flex"}
								alignItems={"center"}
								px={"3px"}
								textColor={"#212121"}
								gap={2}
								onClick={exitLaborPass}
							>
								<IoLogOutOutline
									size={20}
									style={{ transform: "rotate(180deg)" }}
								/>
								<span className="text-lg lg:text-xl">Exit LaborPass</span>
							</Button>
						</HStack>
					</div>
					<div className="w-full grid grid-cols-5 px-4 lg:px-0">
						<div className="col-span-5 lg:col-span-3 flex flex-col border">
							<div className="p-4 flex flex-col gap-4 border-b">
								<Progress
									size="lg"
									value={progress}
									colorScheme={allRequiredStepsComplete ? "green" : "yellow"}
								/>
								<div className="flex flex-col lg:flex-row gap-2 w-full justify-between">
									<p>Your LaborPass setup progress</p>
									<p className=" uppercase">
										<span
											className={clsx({
												"text-green-500": allRequiredStepsComplete,
												"text-yellow-500": !allRequiredStepsComplete,
											})}
										>{`${steps.filter((step) => step.isComplete).length} of ${
											steps.length
										}`}</span>{" "}
										Completed
									</p>
								</div>
							</div>
							<div className=" bg-gray-50">
								{steps.map((step) => (
									<div className="w-full flex justify-between items-center p-4 border-b">
										<div className="flex gap-2">
											{step.isComplete ? (
												<div className="rounded-full flex justify-center items-center border w-6 h-6 bg-green-500">
													<Check className="w-4 h-4 text-white" />
												</div>
											) : (
												<div className="rounded-full border w-6 h-6 bg-white" />
											)}
											<p>
												{step.name}
												{!step.isRequired && (
													<span className="text-gray-500"> (Optional)</span>
												)}
											</p>
										</div>
										{step.cta && !step.isComplete && (
											<RouterLink to={step.cta.url}>
												<div className="flex gap-2 items-center">
													<p className="text-sm">{step.cta.text}</p>
													<ArrowUpRight className="w-4 h-4" />
												</div>
											</RouterLink>
										)}

										{step.isComplete && (
											<p className="text-green-500">Completed</p>
										)}
									</div>
								))}
							</div>
						</div>
						<div className="col-span-2"></div>
					</div>
					<div className="w-full px-4 lg:px-0">
						{!isModifyingToApplyForAJob ? (
							<></>
						) : (
							// <Alert status="info" w={"full"}>
							// 	<div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
							// 		<AlertIcon />
							// 		<p>
							// 			You can easily create and send professional invoices to your
							// 			clients through WhatsApp using our AI powered assistant.{" "}
							// 			<RouterLink to="chat-with-aina" className="underline">
							// 				Chat with Aina
							// 			</RouterLink>
							// 		</p>
							// 	</div>
							// </Alert>
							<Alert status="info" w={"full"}>
								<div className="flex flex-row items-center gap-4">
									<p>
										Done editing your portfolio and ready to go back to your
										application?{" "}
										<span
											onClick={redirectToJobApplicationAfterModification}
											className="cursor-pointer underline"
										>
											Go back
										</span>
									</p>
								</div>
							</Alert>
						)}
					</div>
					<div className="flex flex-col w-full border-brand-50 lg:border-[1px] border-[#C9C9C9]">
						<div className="flex flex-col lg:flex-row lg:justify-between gap-8 border-b-[1px] px-6 py-9">
							<div className="flex items-start gap-5">
								<CRAvatar
									bg="brand.500"
									size={{
										base: "xl",
										lg: "2xl",
									}}
									textColor={"#FEFEFE"}
									name={`${portfolio.firstName} ${portfolio.lastName[0]}.`}
									src={""}
								/>
								<div className="flex flex-col gap-4">
									<div className="flex flex-col gap-1">
										<Heading
											as="h2"
											fontSize={{
												base: 24,
												lg: 28,
											}}
											fontWeight={500}
											textColor={"#040A0D"}
										>
											{portfolio.firstName} {portfolio.lastName[0]}.
										</Heading>
										<div className="flex items-center gap-2">
											<p className="max-w-[160px] lg:max-w-[250px] font-normal text-lg lg:text-xl text-[#464646]">
												{portfolio.title || "No title set"}
											</p>
											<Icon
												as={EditIcon}
												className="w-4 h-4 cursor-pointer"
												onClick={() => {
													localStorage.setItem(
														"redirectAfterCompletion",
														window.location.href
													);
													navigate("/settings");
												}}
											/>
										</div>
										<div className="flex items-center gap-2">
											<p className="max-w-[160px] lg:max-w-[250px] font-normal text-base lg:text-lg text-gray-600">
												{experience}
											</p>
											<Icon
												as={EditIcon}
												className="w-4 h-4 cursor-pointer"
												onClick={() => {
													localStorage.setItem(
														"redirectAfterCompletion",
														window.location.href
													);
													navigate("/settings");
												}}
											/>
										</div>
									</div>

									{portfolio.averageRating > 0 && (
										<div className="flex items-center gap-2">
											<Rating rating={portfolio.averageRating} />
											<p className="text-sm">
												{portfolio.averageRating &&
													portfolio.averageRating.toFixed(2)}{" "}
												<span className="text-gray-500">
													({portfolio.ratingCount} reviews)
												</span>
											</p>
										</div>
									)}
								</div>
							</div>
							<div className="flex flex-col lg:flex-row gap-6 w-full lg:w-auto">
								<Button
									leftIcon={<Share className="w-5 h-5" />}
									colorScheme="prussianBlue"
									size="lg"
									variant="ghost"
									className={clsx("w-full order-2 lg:order-1 lg:w-auto")}
									onClick={handleShare}
								>
									Share Profile
								</Button>
								<Button
									colorScheme="earthRed"
									size="lg"
									variant="outline"
									className={clsx("w-full order-2 lg:order-1 lg:w-auto")}
									onClick={() => window.open(linkToPublicView, "_blank")}
								>
									See Public View
								</Button>
							</div>
						</div>
						<div className="grid grid-cols-1 lg:grid-cols-3 ">
							<div className="col-span-1 flex flex-col">
								<div className="p-6 flex flex-col gap-6 border-b items-start">
									<PortfolioSectionHeading>
										Contact Information
									</PortfolioSectionHeading>
									<div className="flex flex-col gap-4">
										{!portfolio.isEmailHidden && (
											<div className="flex flex-col gap-2">
												<p className="text-base text-[#6E6E6E] font-[Inter]">
													Email address
												</p>
												<p className="text-lg font-[Inter]">
													{portfolio.email}
												</p>
											</div>
										)}
										<div className="flex flex-col gap-2">
											<p className="text-base text-[#6E6E6E] font-[Inter]">
												Phone number
											</p>
											<p className="text-lg font-[Inter]">{portfolio.phone}</p>
										</div>
									</div>
								</div>
								{portfolio.averageRating > 0 && (
									<div className="p-6 grid grid-cols-2 gap-5 border-b items-start">
										<div>
											<p className="font-[Inter] text-lg text-[#121212]">
												Punctuality
											</p>
										</div>
										<Rating
											rating={
												portfolio.averageLaborHackJobsRatings
													.averagePunctualityRating
											}
										/>
										<div>
											<p className="font-[Inter] text-lg text-[#121212]">
												Appearance
											</p>
										</div>
										<Rating
											rating={
												portfolio.averageLaborHackJobsRatings
													.averageAppearanceRating
											}
										/>
										<div>
											<p className="font-[Inter] text-lg text-[#121212]">
												Behaviour
											</p>
										</div>
										<Rating
											rating={
												portfolio.averageLaborHackJobsRatings
													.averageBehaviorRating
											}
										/>
										<div>
											<p className="font-[Inter] text-lg text-[#121212]">
												Proper tool usage
											</p>
										</div>
										<Rating
											rating={
												portfolio.averageLaborHackJobsRatings
													.averageProperToolUsageRating
											}
										/>
										<div>
											<p className="font-[Inter] text-lg text-[#121212]">
												Post service clean up
											</p>
										</div>
										<Rating
											rating={
												portfolio.averageLaborHackJobsRatings
													.averagePostServiceCleanUpRating
											}
										/>
									</div>
								)}
								<div className="p-6 flex gap-5 border-b items-start">
									<div className="w-[64px] h-[64px]">
										<img src={HelmetIcon} alt="Helmet Icon" />
									</div>
									<div className="flex flex-col gap-3">
										<Heading
											as="h3"
											fontSize={{
												base: "22px",
												lg: "26px",
											}}
											textColor={"#040A0D"}
										>
											{noOfEmploymentHistoryItems > 10
												? `10+`
												: noOfEmploymentHistoryItems}
										</Heading>
										<p className="text-base lg:text-lg font-normal font-[Inter] text-[#464646]">
											{noOfEmploymentHistoryItems === 1
												? "Position held"
												: "Positions held"}
										</p>
									</div>
								</div>
								<div className="p-6 flex gap-5 border-b items-start">
									<div className="w-[64px] h-[64px]">
										<img src={HelmetIcon} alt="Helmet Icon" />
									</div>
									<div className="flex flex-col gap-3">
										<Heading
											as="h3"
											fontSize={{
												base: "22px",
												lg: "26px",
											}}
											textColor={"#040A0D"}
										>
											{noOfJobsCompleted > 100 ? `100+` : noOfJobsCompleted}
										</Heading>
										<p className="text-base lg:text-lg font-normal font-[Inter] text-[#464646]">
											{noOfJobsCompleted === 1
												? "Project completed"
												: "Projects completed"}
										</p>
									</div>
								</div>
								<div className="p-6 flex gap-5 border-b items-start">
									<div className="w-[64px] h-[64px]">
										<img src={TransactionsIcon} alt="Transaction Icon" />
									</div>
									<div className="flex flex-col gap-3">
										<Heading
											as="h3"
											fontSize={{
												base: "22px",
												lg: "26px",
											}}
											textColor={"#040A0D"}
										>
											{`${
												canEdit
													? formatAsNaira(averageBudget, 2)
													: formatAsNaira(getCappedAmount(averageBudget), 0)
											}${canEdit ? "" : noOfJobsCompleted > 0 ? "+" : ""}`}
										</Heading>
										<p className="text-base lg:text-lg font-normal font-[Inter] text-[#464646]">
											Average Budget
										</p>
									</div>
								</div>
								{canEdit && (
									<div className="p-6 flex gap-5 border-b items-start">
										<div className="w-[64px] h-[64px]">
											<img src={TransactionsIcon} alt="Transaction Icon" />
										</div>
										<div className="flex flex-col gap-3">
											<Heading
												as="h3"
												fontSize={{
													base: "22px",
													lg: "26px",
												}}
												textColor={"#040A0D"}
											>
												{formatAsNaira(totalBudget, 2)}
											</Heading>
											<p className="text-base lg:text-lg font-normal font-[Inter] text-[#464646]">
												Total Budget
											</p>
										</div>
									</div>
								)}

								<PortfolioBadgeSection
									portfolio={portfolio}
									editMode={canEdit}
								/>
							</div>
							<div className="col-span-1 lg:col-span-2 border-l flex flex-col">
								<PortfolioProfile
									canGenerateProfile={
										(noOfJobsCompleted > 0 || noOfEmploymentHistoryItems > 0) &&
										typeof pro.overview !== "string"
									}
									hasJobs={
										noOfJobsCompleted > 0 || noOfEmploymentHistoryItems > 0
									}
									overview={pro.overview}
								/>
								<div className="w-full border-b py-4">
									<div
										className={clsx("p-6 flex items-start w-full", {
											"justify-between": canEdit,
										})}
									>
										<div>
											<PortfolioSectionHeading>Gallery</PortfolioSectionHeading>
											{portfolioMedia.length > 0 && (
												<p className="text-[#6B7280] text-sm font-normal">
													Tap on an image to view it in full screen.
												</p>
											)}
										</div>
									</div>
									{/* Desktop view gallery */}
									<div
										className={`w-full px-6 hidden lg:block ${
											portfolioMedia.length < 1 ? "py-4" : "py-8"
										}`}
									>
										{portfolioMedia.length >= 3 ? (
											<div className="relative">
												<Slider
													dots={false}
													infinite={true}
													speed={500}
													arrows={true}
													slidesToShow={3}
													slidesToScroll={1}
													autoplay={true}
													autoplaySpeed={3000}
													pauseOnHover={false}
													beforeChange={(oldIndex, newIndex) =>
														setCurrentSlide(newIndex)
													}
													{...carouselSettings}
												>
													{portfolioMedia.map((image, index) => (
														<PortfolioImageContainer
															key={index}
															image={image}
															onEdit={
																canEdit ? handleEditFromGallery : undefined
															}
														/>
													))}
												</Slider>
												<div className="flex justify-center mt-4">
													<div className="bg-black bg-opacity-70 text-white px-3 py-1 rounded-full text-sm">
														{currentSlide + 1} / {portfolioMedia.length}
													</div>
												</div>
											</div>
										) : portfolioMedia.length > 0 ? (
											<div className="grid grid-cols-3 gap-4">
												{portfolioMedia.map((image, index) => (
													<PortfolioImageContainer
														key={index}
														image={image}
														onEdit={handleEditFromGallery}
													/>
												))}
											</div>
										) : (
											<Stack
												border={"1px solid #C9C9C9"}
												p={8}
												display={"flex"}
												flexDirection={"column"}
												alignItems={"center"}
												justifyContent={"center"}
												spacing={3}
												textAlign={"center"}
												w={"full"}
											>
												<NothingToSeeYet />
												<p className="text-[#464646] text-sm lg:text-base font-normal">
													Showcase your work by creating a portfolio. Add
													completed jobs to build your profile.
												</p>
											</Stack>
										)}
									</div>

									{/* Mobile view gallery */}
									<div
										className={`w-full px-6 block lg:hidden ${
											portfolioMedia.length < 1 ? "py-4" : "py-8"
										}`}
									>
										{portfolioMedia.length >= 2 ? (
											<div className="w-full">
												<Slider
													dots={false}
													infinite={true}
													speed={500}
													arrows={true}
													slidesToShow={2}
													slidesToScroll={1}
													autoplay={true}
													autoplaySpeed={3000}
													pauseOnHover={false}
													beforeChange={(oldIndex, newIndex) =>
														setCurrentSlide(newIndex)
													}
													{...carouselSettings}
												>
													{portfolioMedia.map((image, index) => (
														<PortfolioImageContainer
															key={index}
															image={image}
															onEdit={
																canEdit ? handleEditFromGallery : undefined
															}
														/>
													))}
												</Slider>
												<div className="flex justify-center mt-4">
													<div className="bg-black bg-opacity-70 text-white px-3 py-1 rounded-full text-sm">
														{currentSlide + 1} / {portfolioMedia.length}
													</div>
												</div>
											</div>
										) : portfolioMedia.length > 0 ? (
											<div className="grid grid-cols-1 gap-4">
												{portfolioMedia.map((image, index) => (
													<PortfolioImageContainer
														key={index}
														image={image}
														onEdit={handleEditFromGallery}
													/>
												))}
											</div>
										) : (
											<Stack
												border={"1px solid #C9C9C9"}
												p={8}
												display={"flex"}
												flexDirection={"column"}
												alignItems={"center"}
												justifyContent={"center"}
												spacing={3}
												textAlign={"center"}
												w={"full"}
											>
												<NothingToSeeYet />
												<p className="text-[#464646] text-sm lg:text-base font-normal">
													Showcase your work by creating a portfolio. Add
													completed jobs to build your profile.
												</p>
											</Stack>
										)}
									</div>
								</div>

								{(canEdit || !!portfolio.certifications.length) && (
									<div className="p-6 flex flex-col items-start gap-4 border-b ">
										<div
											className={clsx("flex items-center w-full", {
												"justify-between": canEdit,
											})}
										>
											<PortfolioSectionHeading>
												Certifications
											</PortfolioSectionHeading>

											{canEdit && portfolio.certifications.length > 0 && (
												<div className="flex items-center">
													<Link
														href="/my-laborpass/add-certification"
														className="flex items-center gap-2"
														textDecoration={"none"}
														_hover={{ textDecoration: "none" }}
													>
														<Button
															variant="outline"
															leftIcon={<Plus className="w-6 h-6" />}
															colorScheme="earthRed"
														>
															Add
														</Button>
													</Link>
												</div>
											)}
										</div>

										<div className="py-4 w-full">
											{portfolio.certifications.length ? (
												<Certifications
													items={portfolio.certifications}
													editable={canEdit}
												/>
											) : (
												<Stack
													border={"1px solid #C9C9C9"}
													p={8}
													display={"flex"}
													flexDirection={"column"}
													alignItems={"center"}
													justifyContent={"center"}
													spacing={3}
													textAlign={"center"}
													w={"full"}
												>
													<NothingToSeeYet />
													<p className="text-[#464646] text-sm lg:text-base font-normal">
														Certifications help highlight your skills. Upload
														existing certifications or start one with us today.
													</p>
													<div className="flex flex-col lg:flex-row gap-2 items-center">
														{/* <div>
															<Link
																href="/certificates"
																color={"prussianBlue.500"}
																fontSize={"sm"}
															>
																<p className="text-[#77372E] underline text-base lg:text-lg font-medium">
																	Start one with us
																</p>
															</Link>
														</div>
														<Divider
															className="hidden lg:block"
															orientation="vertical"
															h={"20px"}
														/>
														<Divider
															className="block lg:hidden"
															orientation="horizontal"
														/> */}
														<div>
															<Link
																href="/my-laborpass/add-certification"
																color={"prussianBlue.500"}
																fontSize={"sm"}
															>
																<p className="text-[#77372E] underline text-base lg:text-lg font-medium">
																	Upload a certificate
																</p>
															</Link>
														</div>
													</div>
												</Stack>
											)}
										</div>
									</div>
								)}

								<div className="p-6 flex flex-col gap-4 border-b items-start">
									<div
										className={clsx("flex items-center w-full", {
											"justify-between": canEdit,
										})}
									>
										<PortfolioSectionHeading>Education</PortfolioSectionHeading>

										{portfolio.educationEntries.length > 0 && (
											<div className="flex items-center">
												<Link
													href="/my-laborpass/add-education"
													className="flex items-center gap-2"
													textDecoration={"none"}
													_hover={{ textDecoration: "none" }}
												>
													<Button
														variant="outline"
														leftIcon={<Plus className="w-6 h-6" />}
														colorScheme="earthRed"
													>
														Add
													</Button>
												</Link>
											</div>
										)}
									</div>
									{portfolio.educationEntries.length ? (
										<EducationEntries
											items={portfolio.educationEntries}
											editable={canEdit}
										/>
									) : (
										<Stack
											border={"1px solid #C9C9C9"}
											p={8}
											display={"flex"}
											flexDirection={"column"}
											alignItems={"center"}
											justifyContent={"center"}
											spacing={3}
											textAlign={"center"}
											w={"full"}
										>
											<NothingToSeeYet />
											<p className="text-[#464646] text-sm lg:text-base font-normal">
												Education entries help highlight your academic
												background. Add your educational qualifications here.
											</p>
											<div className="flex flex-col lg:flex-row gap-2 items-center">
												<div>
													<Link
														href="/my-laborpass/add-education"
														color={"prussianBlue.500"}
														fontSize={"sm"}
													>
														<p className="text-[#77372E] underline text-base lg:text-lg font-medium">
															Upload an education entry
														</p>
													</Link>
												</div>
											</div>
										</Stack>
									)}
								</div>

								<div className="p-6 flex flex-col gap-4 border-b items-start">
									<div
										className={clsx("flex items-center w-full", {
											"justify-between": canEdit,
										})}
									>
										<PortfolioSectionHeading>
											Work History
										</PortfolioSectionHeading>

										{canEdit && (
											<Link
												href="/my-laborpass/add-job"
												className="flex items-center gap-2"
												textDecoration={"none"}
												_hover={{ textDecoration: "none" }}
											>
												<Button
													variant="outline"
													leftIcon={<Plus className="w-6 h-6" />}
													colorScheme="earthRed"
												>
													Add
												</Button>
											</Link>
										)}
									</div>

									<Tabs variant="line" colorScheme="brand" className="w-full">
										<TabList borderColor={"#C9C9C9"}>
											<Tab
												fontSize={{
													base: "14px",
													lg: "16px",
												}}
												fontFamily={"AeonikPro"}
											>
												Employment history ({noOfEmploymentHistoryItems})
											</Tab>
											<Tab
												fontSize={{
													base: "14px",
													lg: "16px",
												}}
												fontFamily={"AeonikPro"}
											>
												Project history ({noOfJobsCompleted})
											</Tab>
										</TabList>
										<TabPanels>
											<TabPanel p={0}>
												<EmploymentHistory
													items={portfolio?.employmentHistory || []}
													editable={canEdit}
												/>
											</TabPanel>
											<TabPanel p={0}>
												<WorkHistory
													items={portfolio.workHistory || []}
													editable={canEdit}
												/>
											</TabPanel>
										</TabPanels>
									</Tabs>
								</div>
							</div>
						</div>
					</div>
				</Stack>
			</HStack>

			{/* Edit modals for gallery images */}
			{editingWorkItem &&
				(() => {
					if (editingWorkItem.type === "project-history-item") {
						const item = portfolio?.workHistory?.find(
							(wh) => wh.id === editingWorkItem.id
						);
						if (item) {
							return (
								<Modal
									isOpen={!!editingWorkItem}
									onClose={handleCloseEditModal}
									isCentered
									closeOnOverlayClick
									size="lg"
								>
									<ModalOverlay />
									<ModalContent>
										<ModalHeader className="font-[AeonikPro] !text-2xl">
											Edit Project
										</ModalHeader>
										<Divider />
										<ModalBody>
											<WorkHistoryEditor
												item={{ ...item, id: editingWorkItem.id }}
												onClose={handleCloseEditModal}
											/>
										</ModalBody>
									</ModalContent>
								</Modal>
							);
						}
					} else if (editingWorkItem.type === "employment-history-item") {
						const item = portfolio?.employmentHistory?.find(
							(eh) => eh.id === editingWorkItem.id
						);
						if (item) {
							return (
								<Modal
									isOpen={!!editingWorkItem}
									onClose={handleCloseEditModal}
									isCentered
									closeOnOverlayClick
									size="lg"
								>
									<ModalOverlay />
									<ModalContent>
										<ModalHeader className="font-[AeonikPro] !text-2xl">
											Edit Employment
										</ModalHeader>
										<Divider />
										<ModalBody>
											<EmploymentHistoryEditor
												item={item}
												onClose={handleCloseEditModal}
											/>
										</ModalBody>
									</ModalContent>
								</Modal>
							);
						}
					}
					return null;
				})()}
		</>
	);
}
